import { DateTime, Duration } from 'luxon';
import { ProductParsers } from '../product/product-parsers';
import {
  ScheduledService,
  ScheduledServiceProductUnit,
  UseabilityStatus,
  WorkOrder,
  WorkOrderNoteFile,
  WorkOrderProduct,
  WorkOrderStatus,
  WorkOrderType,
} from './service-and-repair';
import { UserParsers } from '../user/user-parsers';
import { InvoiceParsers } from '../invoice/invoice-parsers';
import { AdminParsers } from '../admin/admin-parsers';

export namespace WorkOrderParsers {
  export class WorkOrderJson {
    id!: string;
    orderNumber: string | undefined;
    poNumber: string | undefined;
    locationId: number | undefined;
    from!: string;
    to: string | undefined;
    actualStart: string | undefined;
    actualEnd: string | undefined;
    product: ProductParsers.ProductJson | undefined;
    unit: ProductParsers.ProductInventoryUnitJson | undefined;
    unitStatus: UseabilityStatus | undefined;
    user: UserParsers.UserJson | undefined;
    userEquipment: UserParsers.UserEquipmentJson | undefined;
    equipmentDescription: string | undefined;
    type: WorkOrderParsers.WorkOrderTypeJson | undefined;
    status!: WorkOrderStatus;
    actualCost: number | undefined;
    hourStart: string | undefined;
    notes: string | undefined;
    invoices: InvoiceParsers.InvoiceJson[] | undefined;
    latestNote?: WorkOrderNoteFileJson;
    mechanic?: AdminParsers.AdminJson;
    workOrderProducts!: WorkOrderProductJson[];
    createdAt!: string;
    updatedAt!: string;
  }

  export class WorkOrderNoteFileJson {
    id!: string;
    workOrderId!: string;
    note?: string;
    file?: string;
    uploadedBy!: {
      adminId: number;
      adminFullName: string;
    };
    uploadedAt!: string;
  }

  export class WorkOrderProductJson {
    id!: string;
    workOrderId!: string;
    product!: ProductParsers.ProductJson;
    quantity!: number;
    customerUnitPrice!: number;
    description: string | undefined;
    createdAt!: string;
    updatedAt!: string;
  }

  export class WorkOrderTypeJson {
    id!: string;
    label!: string;
  }

  export function toWorkOrder(input: WorkOrderJson): WorkOrder {
    return {
      ...input,
      from: DateTime.fromISO(input.from),
      to: input.to ? DateTime.fromISO(input.to) : undefined,
      actualStart: input.actualStart ? DateTime.fromISO(input.actualStart) : undefined,
      actualEnd: input.actualEnd ? DateTime.fromISO(input.actualEnd) : undefined,
      product: input.product ? ProductParsers.toProduct(input.product) : undefined,
      unit: input.unit ? ProductParsers.toProductInventoryUnit(input.unit) : undefined,
      user: input.user ? UserParsers.toUser(input.user) : undefined,
      userEquipment: input.userEquipment ? UserParsers.toUserEquipment(input.userEquipment) : undefined,
      type: input.type ? WorkOrderParsers.toWorkOrderType(input.type) : undefined,
      hourStart: input.hourStart ? DateTime.fromISO(input.hourStart) : undefined,
      notes: input.notes,
      invoices: input.invoices ? input.invoices.map((inv) => InvoiceParsers.toInvoice(inv)) : undefined,
      latestNote: input.latestNote ? WorkOrderParsers.toWorkOrderAttachment(input.latestNote) : undefined,
      mechanic: input.mechanic ? AdminParsers.toAdmin(input.mechanic) : undefined,
      workOrderProducts: input.workOrderProducts.map((wop) => WorkOrderParsers.toWorkOrderProduct(wop)),
      createdAt: DateTime.fromISO(input.createdAt),
      updatedAt: DateTime.fromISO(input.updatedAt),
    };
  }

  export function toWorkOrderAttachment(input: WorkOrderNoteFileJson): WorkOrderNoteFile {
    return {
      ...input,
      uploadedAt: DateTime.fromISO(input.uploadedAt),
    };
  }

  export function toWorkOrderProduct(input: WorkOrderProductJson): WorkOrderProduct {
    return {
      id: input.id,
      workOrderId: input.workOrderId,
      product: ProductParsers.toProduct(input.product),
      quantity: input.quantity,
      customerUnitPrice: input.customerUnitPrice,
      description: input.description,
      createdAt: DateTime.fromISO(input.createdAt),
      updatedAt: DateTime.fromISO(input.updatedAt),
    };
  }

  export function toWorkOrderType(input: WorkOrderTypeJson): WorkOrderType {
    return {
      id: input.id,
      label: input.label,
    };
  }
}

export namespace ScheduledServiceParsers {
  export class ScheduledServiceJson {
    id!: string;
    serviceName!: string;
    workOrderType!: WorkOrderType;
    unitStatus!: string;
    duration?: string;
    frequencyInterval?: string;
    machineHoursInterval?: number;
    createdAt!: string;
    createdBy!: number;
    updatedAt!: string;
    updatedBy!: number;
    deleted!: boolean;
    productUnits!: { [productId: number]: { name: string; units: ScheduledServiceProductUnitJson[] } };
  }

  export class ScheduledServiceProductUnitJson {
    productUnit!: ProductParsers.ProductInventoryUnitJson;
    initialServiceDate?: string;
    initialServiceHours?: number;
  }

  export function toScheduledService(input: ScheduledServiceJson): ScheduledService {
    return {
      ...input,
      unitStatus: input.unitStatus === 'Hard Down' ? UseabilityStatus.HardDown : UseabilityStatus.SoftDown,
      workOrderType: WorkOrderParsers.toWorkOrderType(input.workOrderType),
      duration: input.duration ? Duration.fromISO(input.duration) : undefined,
      frequencyInterval: input.frequencyInterval ? Duration.fromISO(input.frequencyInterval) : undefined,
      createdAt: DateTime.fromISO(input.createdAt),
      updatedAt: DateTime.fromISO(input.updatedAt),
      productUnits: Object.entries(input.productUnits).reduce((acc, [productId, units]) => {
        acc[parseInt(productId)] = {
          name: units.name,
          units: units.units.map((unit) => toScheduledServiceProductUnit(unit)),
        };

        return acc;
      }, {} as { [productId: number]: { name: string; units: ScheduledServiceProductUnit[] } }),
    };
  }

  function toScheduledServiceProductUnit(input: ScheduledServiceProductUnitJson): ScheduledServiceProductUnit {
    return {
      productUnit: ProductParsers.toProductInventoryUnit(input.productUnit),
      initialServiceDate: input.initialServiceDate ? DateTime.fromISO(input.initialServiceDate) : undefined,
      initialServiceHours: input.initialServiceHours,
    };
  }
}
